import map from 'lodash/map'
import React, { Dispatch, SetStateAction, useContext } from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Looks } from '@Components/Looks'
import { ProductContext } from '@Components/Product/Context'
import { ComplementaryProducts } from '@Domains/Product/Features/ComplementaryProducts/ComplementaryProducts'
import { ProductBundle } from '@Domains/Product/Features/Display/ProductBundle/ProductBundle'
import { ProductVariations } from '@Domains/Product/Features/Display/ProductVariations/ProductVariations'
import { RelatedProducts } from '@Domains/Product/Features/Display/RelatedProducts/RelatedProducts'
import { UpSellItem } from '@Domains/Product/Features/Display/UpSellItem/UpSellItem'
import { ProductSlider } from '@Domains/Product/Features/ProductSlider/ProductSlider'
import { AssortmentLadder } from '@Domains/Product/Features/Tactics/Components/AssortmentLadder/AssortmentLadder'
import { Blocks } from '@Domains/Product/Features/Tactics/Components/Blocks/Blocks'
import { BuildYourDiscountTactic } from '@Domains/Product/Features/Tactics/Components/BuildYourDiscount/BuildYourDiscountTactic'
import { LinkedProducts } from '@Domains/Product/Features/Tactics/Components/LinkedProducts'
import { MatchingVariantTactic } from '@Domains/Product/Features/Tactics/Components/MatchingVariant/ProductPage'
import { ProductQuantityDiscount } from '@Domains/Product/Features/Tactics/Components/ProductQuantityDiscount/ProductQuantityDiscount'
import { RecommendationGrid } from '@Domains/Product/Features/Tactics/Components/Recommendation/Grid'
import { RecommendationSlider } from '@Domains/Product/Features/Tactics/Components/Recommendation/Slider'
import { UpgradeWith } from '@Domains/Product/Features/Tactics/Components/UpgradeWith/ProductPage'
import { AttributePrice, Product, ProductPageTacticFragmentFragment, ProductWithQuantity } from '@Generated/graphql'
import { GlobalAnchorItemInjections } from '@Globals/Injections/Client'
import { useTranslation } from '@Globals/Translations'

import styles from './ProductTactics.module.scss'

const ProductTactics = ({
  addonsSelected = false,
  buyPersonalizationID,
  group,
  onBuyPersonalizationID,
  selectedCrossSellIDs,
  setSelectedCrossSellIDs,
  selectedRelatedProductIDs = [],
  setSelectedRelatedProductIDs,
}: {
  addonsSelected?: boolean
  buyPersonalizationID?: string | null
  group: Exclude<keyof NonNullable<Product['tactics']>, '__typename'>
  onBuyPersonalizationID?: (personalizationID: string | null) => void
  selectedCrossSellIDs?: Product['id'][]
  selectedRelatedProductIDs?: string[]
  setSelectedCrossSellIDs?: SetStateAction<any>
  setSelectedRelatedProductIDs?: Dispatch<SetStateAction<string[]>>
}) => {
  const { t } = useTranslation()
  const product = useContext(ProductContext)

  if (!product) {
    return null
  }

  const tactics = product?.tactics?.[group] as ProductPageTacticFragmentFragment[] | undefined
  if (!tactics || tactics.length === 0) {
    return null
  }

  return tactics.map((tactic) => {
    if (tactic.__typename === 'TacticActiveCampaign') {
      return (
        <GlobalAnchorItemInjections
          key={`${tactic.__typename}_${tactic.id}`}
          anchor="tactic_active_campaign"
        />
      )
    }

    if (tactic.__typename === 'TacticAssortmentLadder') {
      return (
        <AssortmentLadder
          key={`${tactic.__typename}_${tactic.id}`}
          tactic={tactic as any}
        />
      )
    }

    if (tactic.__typename === 'TacticBlocks') {
      return (
        <Blocks
          key={`${tactic.__typename}_${tactic.id}`}
          tactic={tactic as any}
        />
      )
    }

    if (tactic.__typename === 'TacticBuildYourDiscount') {
      return (
        <BuildYourDiscountTactic
          key={`${tactic.__typename}_${tactic.id}`}
          disable={addonsSelected}
          primaryProduct={product}
          tactic={tactic}
        />
      )
    }

    if (tactic.__typename === 'TacticBundleInfo') {
      const settings = (tactic.settings || {}) as Record<string, any>
      return (
        <ProductBundle
          bundleItems={tactic.bundleProducts as ProductWithQuantity[]}
          bundlePrice={tactic.bundlePrice as AttributePrice}
          disablePrices={Boolean(settings.disablePrices)}
          disableProductLinks={Boolean(settings.disableProductLinks)}
        />
      )
    }

    if (tactic.__typename === 'TacticComplementaryProducts') {
      if (!tactic.products || tactic.products.length === 0 || !setSelectedRelatedProductIDs) {
        return null
      }

      return (
        <ComplementaryProducts
          key={`${tactic.__typename}_${tactic.id}`}
          header={t('products:WB_RELATED_PRODUCTS_LABEL')}
        >
          {map(tactic.products, relatedProduct => (
            <UpSellItem
              key={`ComplementaryProduct_${relatedProduct.id}`}
              id={relatedProduct.id}
              onChange={() => {
                if (selectedRelatedProductIDs.includes(relatedProduct.id)) {
                  setSelectedRelatedProductIDs(prev => prev.filter(a => a !== relatedProduct.id))
                } else {
                  setSelectedRelatedProductIDs(prev => [...prev, relatedProduct.id])
                }
              }}
              product={relatedProduct}
              selected={selectedRelatedProductIDs.includes(relatedProduct.id)}
            />
          ))}
        </ComplementaryProducts>
      )
    }

    if (tactic.__typename === 'TacticLinkedProductsColor') {
      return (
        <LinkedProducts
          key={`${tactic.__typename}_${tactic.id}`}
          products={tactic.color?.products}
          type="color"
        />
      )
    }

    if (tactic.__typename === 'TacticLinkedProductsSizeAndVariation') {
      if ((!tactic.size?.products || tactic.size.products.length === 0)
        && (!tactic.variation?.products || tactic.variation.products.length === 0)
      ) {
        return null
      }

      return (
        <ProductVariations
          key={`${tactic.__typename}_${tactic.id}`}
          sizes={tactic.size?.products || []}
          variations={tactic.variation?.products || []}
        />
      )
    }

    if (tactic.__typename === 'TacticLooks') {
      if (!tactic.looks || tactic.looks.length === 0) {
        return null
      }

      return (
        <Looks
          key={`${tactic.__typename}_${tactic.id}`}
          id={tactic.id || undefined}
          looks={tactic.looks as any}
          settings={tactic.settings || undefined}
          title={tactic.title?.defaults || undefined}
        />
      )
    }

    if (tactic.__typename === 'TacticMatchingVariant') {
      return (
        <MatchingVariantTactic
          key={`${tactic.__typename}_${tactic.id}`}
          tactic={tactic}
        />
      )
    }

    if (tactic.__typename === 'TacticOthersBought') {
      return (
        <RecommendationSlider
          key={`${tactic.__typename}_${tactic.id}`}
          group={group}
          id={tactic.id || ''}
          productID={product.id}
        />
      )
    }

    if (tactic.__typename === 'TacticOthersBoughtGrid') {
      return (
        <RecommendationGrid
          key={`${tactic.__typename}_${tactic.id}`}
          group={group}
          id={tactic.id || ''}
          productID={product.id}
        />
      )
    }

    if (tactic.__typename === 'TacticRecommendedForYou') {
      return (
        <RecommendationSlider
          key={`${tactic.__typename}_${tactic.id}`}
          group={group}
          id={tactic.id || ''}
          productID={product.id}
        />
      )
    }

    if (tactic.__typename === 'TacticRelatedProducts') {
      if (!tactic.products || tactic.products.length === 0) {
        return null
      }

      return (
        <RelatedProducts
          key={`${tactic.__typename}_${tactic.id}`}
          products={tactic.products}
          sourceID="linked_cross_sales_products"
        />
      )
    }

    if (tactic.__typename === 'TacticRelativeDiscount') {
      return (
        <ProductQuantityDiscount
          key={`${tactic.__typename}_${tactic.id}`}
          primaryProduct={product}
          tactic={tactic as any}
        />
      )
    }

    if (tactic.__typename === 'TacticSimilarProducts') {
      if (!tactic.products || tactic.products.length === 0) {
        return null
      }

      return (
        <BlockContainer
          key={`${tactic.__typename}_${tactic.id}`}
          appearance={{
            backgroundColor: 'var(--green-500)',
            textColor: 'var(--white)',
          }}
          className={styles.productSlider}
          common={{
            position: 'left',
            title: t('products:SIMILAR_PRODUCTS_LABEL'),
            titleElement: 'h2',
            text: {
              key: 'cms:SIMILAR_PRODUCTS_LABEL',
              value: {
                children: [
                  {
                    children: [
                      {
                        text: t('products:OTHER_CUSTOMERS_ALSO_BOUGHT'),
                      },
                    ],
                    type: 'p',
                  },
                ],
              },
            },
          }}
        >
          <ProductSlider
            className={styles.slider}
            products={tactic.products}
            sourceID="house_brand"
          />
        </BlockContainer>
      )
    }

    if (tactic.__typename === 'TacticUpgradeWith') {
      if (!tactic.items || tactic.items.length === 0 || !onBuyPersonalizationID || !setSelectedCrossSellIDs) {
        return null
      }

      return (
        <UpgradeWith
          key={`${tactic.__typename}_${tactic.id}`}
          buyPersonalizationID={buyPersonalizationID}
          items={tactic.items}
          onBuyPersonalizationID={onBuyPersonalizationID}
          selectedCrossSellIDs={selectedCrossSellIDs}
          setSelectedCrossSellIDs={setSelectedCrossSellIDs}
        />
      )
    }

    return null
  })
}

export default ProductTactics
